import { isFunction, isPlainObject, merge } from 'lodash-es'
import { closeFloatingPreview } from '../../../app-state/modals'
import { setFloatingPreview } from '../../../app-state/modals/state.floater'
import { getScreenState } from '../../../app-state/state.screen'
import { FloaterSize } from '../../../constants/enums'
import { FLOATING_PREVIEW } from '../../../constants/floaters'
import { palette } from '../../styles/colors'
import Avatar from './Displays/Avatar'
import Detailed from './Displays/Detailed'
import Preview from './Displays/Preview'
import Text from './Displays/Text'
import { DESKTOP_AVATAR_STYLES, MOBILE_AVATAR_STYLES } from './styles/avatar'
import {
    DESKTOP_DETAILED_STYLES,
    MOBILE_DETAILED_STYLES,
} from './styles/detailed'
import { DESKTOP_LIST_STYLES, MOBILE_LIST_STYLES } from './styles/list'
import { DESKTOP_PREVIEW_STYLES, MOBILE_PREVIEW_STYLES } from './styles/preview'
import { DESKTOP_TEXT_STYLES, MOBILE_TEXT_STYLES } from './styles/text'

const STYLES = {
    desktop: merge(
        {},
        DESKTOP_AVATAR_STYLES,
        DESKTOP_PREVIEW_STYLES,
        DESKTOP_TEXT_STYLES,
        DESKTOP_DETAILED_STYLES,
        DESKTOP_LIST_STYLES,
    ),
    mobile: merge(
        {},
        MOBILE_AVATAR_STYLES,
        MOBILE_PREVIEW_STYLES,
        MOBILE_TEXT_STYLES,
        MOBILE_DETAILED_STYLES,
        MOBILE_LIST_STYLES,
    ),
}

export type GetStylesConfig = {
    debug?: boolean
    px?: boolean
}

export type GetStylesData = {
    gridPadding?: number
    columnWidth?: number
    columnCount?: number
    chain?: Array<object>
}

export const getStyle = (
    key,
    config: GetStylesConfig = {},
    data: GetStylesData = {},
    styles = STYLES,
) => {
    const { debug } = config

    if (debug) {
        console.log('key', key, JSON.stringify(styles, null, 4))
    }

    const style = styles[key]

    if (isPlainObject(style)) {
        const { chain = [] } = data
        const isRoot = styles === STYLES
        const newChain = isRoot ? [styles, STYLES] : [styles, ...chain]
        const newData = { ...data, chain: newChain }

        return (key, overrideConfig) =>
            getStyle(key, overrideConfig || config, newData, style)
    }

    if (isFunction(style)) {
        return () =>
            style({
                ...data,
                parent: styles,
                styles: STYLES,
                config,
            })
    }

    const { px = false } = config

    if (typeof style === 'number' && px) {
        return style + 'px'
    }

    return style
}

export const getRowHeight = (config) => {
    const {
        displayHeight,
        displayBanner,
        gridPadding,
        platform,
        display,
        scale,
    } = config

    const target = STYLES[platform][scale][display]
    const knownHeight = target._knownHeight(target, { displayBanner })
    return gridPadding + displayHeight + knownHeight
}

export const getDisplayHeight = (config) => {
    const { columnWidth, display } = config

    if (display === 'preview' || display === 'detailed') {
        return columnWidth * (9 / 16)
    }

    if (display === 'text' || display === 'list') {
        return 0
    }

    return columnWidth
}

export const getGridWidthAndColumnCount = (containerWidth, maxColumnWidth) => {
    const columnCount = Math.ceil(containerWidth / maxColumnWidth)
    const columnWidth = roundDecimals(containerWidth / columnCount)

    return { columnWidth, columnCount }
}

export const getGridSpecs = (config) => {
    const {
        isTouchDevice,
        gridPadding,
        enableSlide,
        platform,
        display,
        scale,
        width,
    } = config

    const maxColumnWidth = STYLES[platform][scale][display].column.maxWidth
    const { columnWidth, columnCount } = getGridWidthAndColumnCount(
        width - gridPadding,
        maxColumnWidth,
    )

    if (!isTouchDevice || !enableSlide) {
        return { columnWidth, columnCount }
    }

    const result = getGridWidthAndColumnCount(
        width + columnWidth / 2 - gridPadding,
        maxColumnWidth,
    )
    const { columnCount: newColumnCount } = result
    const multiplier = scale === 'sm' ? 4 : scale === 'md' ? 5 : 7

    if (newColumnCount === 1 && scale === 'lg') {
        return {
            columnWidth: width - columnWidth / 6,
            columnCount: newColumnCount * multiplier,
        }
    }

    return { ...result, columnCount: newColumnCount * multiplier }
}

export const getScale = (density) => {
    const small = density === 1 ? 'sm' : null
    const medium = density === 2 ? 'md' : null
    const large = density === 3 ? 'lg' : null

    const scale = small || medium || large

    return scale
}

export const getGridPadding = (scale) => {
    if (scale === 'sm') {
        return 4
    }

    if (scale === 'md') {
        return 6
    }

    if (scale === 'lg') {
        return 8
    }
}

export const resolveModelStatus = (user) => {
    const { isTruePrivate, isPrivateShow, isGroupShow, isClubShow, isAway } =
        user

    if (isTruePrivate) {
        return 'TruePrivate'
    }

    if (isPrivateShow) {
        return 'Private'
    }

    if (isGroupShow) {
        return 'Group Show'
    }

    if (isClubShow) {
        return 'Club Show'
    }

    if (isAway) {
        return 'Away'
    }

    return null
}

export const TIMEOUT_ANIMATION_AVATAR = 250

export const roundDecimals = (n) =>
    Math.round((n + Number.EPSILON) * 10000) / 10000

export const resolveDisplayContainer = (display) => {
    if (display === 'preview') {
        return Preview
    }

    if (display === 'text') {
        return Text
    }

    if (display === 'detailed') {
        return Detailed
    }

    return Avatar
}

export const resolveDisplayHighlight = (config) => {
    const {
        displayRecentlyVisitedHighlight,
        displayActiveRoomHighlight,
        displayTrendingHighlight,
        displayBookmarkHighlight,
        displayPopularHighlight,
        displayFriendHighlight,
    } = config

    if (displayActiveRoomHighlight) {
        return palette.highlightActiveRoom
    }

    if (displayFriendHighlight) {
        return palette.badgeFriend
    }

    if (displayBookmarkHighlight) {
        return palette.badgeBookmark
    }

    if (displayPopularHighlight) {
        return palette.badgePopular
    }

    if (displayTrendingHighlight) {
        return palette.badgeTrending
    }

    if (displayRecentlyVisitedHighlight) {
        return '#E0E0E0'
    }

    return null
}

export const resolveButtonHighlight = (config) => {
    const { isBookmark, isFriend } = config

    if (isBookmark) {
        return palette.badgeBookmark
    }

    if (isFriend) {
        return palette.badgeFriend
    }

    return '#F6F6F6'
}

const calcLargePreviewLocation = (target?: HTMLElement) => {
    const { left: buttonPositionLeft = 0 } =
        target?.getBoundingClientRect?.() ?? {}
    const buffer = 50
    const { windowWidth, windowHeight } = getScreenState()
    const halfScreen = (windowWidth || 800) / 2
    const rightPlacement = buttonPositionLeft < halfScreen
    const tentativeWidth = Math.max(halfScreen - buffer * 2, 300)
    const positionLeft =
        buttonPositionLeft +
        (rightPlacement ? buffer : -(buffer + tentativeWidth))

    return {
        width: tentativeWidth,
        x: positionLeft,
        y: windowHeight / 4,
    }
}

export const setLargePreview = (
    id: number,
    target: HTMLElement,
    pinned = false,
    show = true,
) => {
    if (!show) {
        closeFloatingPreview(id)
        return
    }
    const dimensions = calcLargePreviewLocation(target)
    setFloatingPreview(
        id,
        {
            show,
            type: FLOATING_PREVIEW,
            close: pinned,
            sticky: false,
            stickyHeader: pinned,
            size: FloaterSize.Large,
        },
        dimensions,
    )
}

export const isAnonymousMissMFC = (slugOrId) => {
    return String(slugOrId).startsWith('_')
}

export const getAnonymousMissMFCRank = (slug): number => {
    return Number(slug.split('_')[1])
}
